import { Trans, useLingui } from '@lingui/react/macro';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  formatLastUsed,
  formatRelativeDate,
  formatSiteNames,
  getTemplateStats,
} from '@/domain/templates/template-utils';
import { cn } from '@/lib/utils';
import type { SavedTemplate } from '@/types/saved-template';

import { StatusBadge } from './status-badge';
import { TemplatesRowActions } from './templates-row-actions';

interface TemplatesTableProps {
  templates: SavedTemplate[];
  selection: Set<string>;
  onSelectionChange: (next: Set<string>) => void;
  /** Template just activated in the workspace — highlighted on return. */
  justActivatedId?: string;
  onPreview: (template: SavedTemplate) => void;
  onEdit: (template: SavedTemplate) => void;
  onDuplicate: (template: SavedTemplate) => void;
  onApplyToRota: (template: SavedTemplate) => void;
  onDelete: (template: SavedTemplate) => void;
  onPublish: (template: SavedTemplate) => void;
  onUnpublish: (template: SavedTemplate) => void;
  onRestore: (template: SavedTemplate) => void;
  onExport: (template: SavedTemplate) => void;
}

/**
 * Main list of templates on the landing page (Task 6.12, L5/L15/L16).
 * Renders one row per template with selection checkbox, status badge,
 * resolved site names, weekly stats, modified/last-used relative
 * dates, and a per-row dropdown of actions.
 *
 * Selection state is owned by the parent (URL or React state — the
 * landing route keeps it local). Templates must already be enriched
 * with `siteNames` upstream via `enrichTemplatesWithSiteNames`; this
 * component does not resolve site IDs itself.
 */
export function TemplatesTable(props: TemplatesTableProps) {
  const { t } = useLingui();
  const { templates, selection, onSelectionChange, justActivatedId } = props;
  const allSelected = templates.length > 0 && templates.every((tpl) => selection.has(tpl.id));
  // Radix's Checkbox accepts `boolean | 'indeterminate'`. Show the
  // tri-state form when some — but not all — visible rows are
  // selected so the header reflects partial-selection clearly.
  const headerChecked: boolean | 'indeterminate' = allSelected
    ? true
    : selection.size > 0
      ? 'indeterminate'
      : false;

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-10">
            <Checkbox
              aria-label={t`Select all`}
              checked={headerChecked}
              onCheckedChange={() => toggleAll(templates, allSelected, onSelectionChange)}
            />
          </TableHead>
          <TableHead>
            <Trans>Name</Trans>
          </TableHead>
          <TableHead>
            <Trans>Sites</Trans>
          </TableHead>
          <TableHead className="text-right">
            <Trans>Shifts</Trans>
          </TableHead>
          <TableHead className="text-right">
            <Trans>Hours</Trans>
          </TableHead>
          <TableHead>
            <Trans>Status</Trans>
          </TableHead>
          <TableHead>
            <Trans>Last used</Trans>
          </TableHead>
          <TableHead>
            <Trans>Last modified</Trans>
          </TableHead>
          <TableHead className="text-right">
            <Trans>Actions</Trans>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {templates.map((tpl) => {
          const stats = getTemplateStats(tpl);
          const sites = formatSiteNames(tpl.scope);
          const lastUsed = formatLastUsed(tpl.lastUsedAt);
          // Bind to named locals so the macro extracts the message
          // with `{name}` / `{totalShifts}` / `{hours}` rather than
          // positional `{0}` / `{1}` (gives translators something
          // semantic to anchor on).
          const name = tpl.name;
          const { totalShifts } = stats;
          const hours = stats.totalHoursPerWeek.toFixed(1);
          const isActivated = tpl.id === justActivatedId;
          // The API returns 404 when a deleted template is opened, so a
          // deleted row can't be opened for editing.
          const isDeleted = tpl.status === 'deleted';
          return (
            <TableRow
              key={tpl.id}
              className={cn(
                !isDeleted && 'cursor-pointer',
                isActivated && 'bg-accent/50 hover:bg-accent/50',
              )}
              onClick={isDeleted ? undefined : () => props.onEdit(tpl)}
            >
              <TableCell onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  aria-label={t`Select ${name}`}
                  checked={selection.has(tpl.id)}
                  onCheckedChange={() => toggleRow(selection, tpl.id, onSelectionChange)}
                />
              </TableCell>
              <TableCell>
                {/* Concrete max-width + `truncate` (block overrides the
                    button's inline-flex via tailwind-merge) caps the name so a
                    long one ellipsises instead of forcing the table to scroll
                    horizontally. The full name is on the `title` for hover.

                    The constraint-critical utilities carry `!important` (the
                    Tailwind v4 `!` suffix) because when the library is embedded
                    in a host (WFM), the host's global CSS can otherwise win over
                    our single-class utilities and drop `max-width`/`overflow`,
                    letting the name render full-width. Forcing display +
                    max-width + overflow + white-space makes truncation hold
                    regardless of host styles. `truncate` is kept for the same
                    declarations at normal priority (and the spec asserts it). */}
                {isDeleted ? (
                  <span
                    className="text-foreground block! max-w-[240px]! truncate overflow-hidden! whitespace-nowrap! text-left font-medium lg:max-w-[360px]!"
                    title={name}
                  >
                    {tpl.name}
                  </span>
                ) : (
                  <Button
                    variant="link"
                    className="text-foreground hover:text-foreground block! h-auto max-w-[240px]! truncate overflow-hidden! whitespace-nowrap! p-0 text-left font-medium hover:underline lg:max-w-[360px]!"
                    onClick={(e) => {
                      e.stopPropagation();
                      props.onEdit(tpl);
                    }}
                    title={name}
                  >
                    {tpl.name}
                  </Button>
                )}
                {isActivated && (
                  <span className="text-xs font-medium text-primary">
                    <Trans>Just published</Trans>
                  </span>
                )}
              </TableCell>
              <TableCell className="text-foreground" title={sites.tooltip ?? undefined}>
                {sites.display}
              </TableCell>
              <TableCell
                data-testid="row-shifts"
                className="text-right text-foreground tabular-nums"
              >
                {totalShifts === 0 ? '—' : totalShifts}
              </TableCell>
              <TableCell
                data-testid="row-hours"
                className="text-right text-foreground tabular-nums"
              >
                {totalShifts === 0 ? '—' : hours}
              </TableCell>
              <TableCell>
                <StatusBadge status={tpl.status} />
              </TableCell>
              <TableCell className="text-foreground" title={lastUsed.tooltip ?? undefined}>
                {lastUsed.display}
              </TableCell>
              <TableCell className="text-foreground">
                {formatRelativeDate(tpl.modifiedAt)}
              </TableCell>
              <TableCell onClick={(e) => e.stopPropagation()}>
                <TemplatesRowActions
                  template={tpl}
                  onPreview={props.onPreview}
                  onEdit={props.onEdit}
                  onDuplicate={props.onDuplicate}
                  onApplyToRota={props.onApplyToRota}
                  onDelete={props.onDelete}
                  onPublish={props.onPublish}
                  onUnpublish={props.onUnpublish}
                  onRestore={props.onRestore}
                  onExport={props.onExport}
                />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

/**
 * Header checkbox handler. Clears the selection when every row is
 * already selected; otherwise selects every visible row.
 */
function toggleAll(
  templates: SavedTemplate[],
  allSelected: boolean,
  onSelectionChange: (next: Set<string>) => void,
) {
  if (allSelected) onSelectionChange(new Set());
  else onSelectionChange(new Set(templates.map((tpl) => tpl.id)));
}

/**
 * Per-row checkbox handler. Adds the row's id when missing, removes
 * it when present, and notifies the parent with a fresh `Set`.
 */
function toggleRow(
  current: Set<string>,
  id: string,
  onSelectionChange: (next: Set<string>) => void,
) {
  const next = new Set(current);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  onSelectionChange(next);
}
